import React from "react";
import PropTypes from 'prop-types';
// @mui
import {styled, useTheme} from '@mui/material/styles';
import CardContent from "@mui/material/CardContent";
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
// components
import Chart, {useChart} from '../../../components/chart';
import {fPercentEuro} from "../../../utils/format-number";

// ----------------------------------------------------------------------

const CHART_HEIGHT = 480;
const LEGEND_HEIGHT = 30;
const CHART_AREA_HEIGHT = CHART_HEIGHT - LEGEND_HEIGHT; // New constant

const ChartContainer = styled(Box)({
    position: 'relative',
    height: CHART_HEIGHT,
    width: '100%',
});

const LegendContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: LEGEND_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    zIndex: 10,
}));

const StyledChart = styled(Chart)({
    height: `${CHART_AREA_HEIGHT}px !important`,
    width: '100% !important',
    '& .apexcharts-canvas, .apexcharts-inner, svg, foreignObject': {
        height: `${CHART_AREA_HEIGHT}px !important`,
        width: '100% !important',
    },
    '& .apexcharts-legend': {
        display: 'none !important',
    },
});

export default function ChartRadar({title, series, categories}) {
    const theme = useTheme();
    const allValues = series.flatMap(s => s.data);
    const maxValue = Math.min(Math.max(...allValues), 100);
    const dynamicMax = maxValue < 60 ? 60 : Math.ceil(maxValue / 10) * 10;

    const seriesColors = [theme.palette.primary.main, theme.palette.tertiary.main];

    // Capitalize first letter of each category
    const capitalizedCategories = categories.map(
        (cat) => cat.charAt(0).toUpperCase() + cat.slice(1)
    );

    const chartOptions = useChart({
        colors: seriesColors,
        markers: {
            size: 3
        },
        stroke: {
            width: 2,
        },
        fill: {
            opacity: 0.48,
        },
        tooltip: {
            enabled: true,
            x: {
                show: true,
            },
            y: {
                formatter: (value) => fPercentEuro(value || '0'),
            },
        },
        legend: {
            show: false, // Disable built-in legend
        },
        xaxis: {
            categories: capitalizedCategories,
            labels: {
                style: {
                    colors: categories.map(() => theme.palette.grey["900"]),
                    fontWeight: 600,
                    fontSize: '.8rem',
                },
            },
        },
        yaxis: {
            min: 0,
            max: dynamicMax,
            tickAmount: 5,
            labels: {
                style: {
                    colors: theme.palette.grey["700"]
                },
                formatter: (value) => fPercentEuro(value || '0'),
            }
        }
    });

    return (
   <Box sx={{ flex: 1, width: '100%', height: '100%' }}>
        <Typography variant="h6" mb={1}>{title}</Typography>
        <ChartContainer>
            <StyledChart 
                dir="ltr" 
                type="radar" 
                series={series} 
                options={chartOptions} 
                height={CHART_AREA_HEIGHT} // Add this prop
            />
            <LegendContainer>
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {series.map((s, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                                sx={{ 
                                    width: 12, 
                                    height: 12, 
                                    backgroundColor: seriesColors[index],
                                    borderRadius: '50%' 
                                }} 
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {s.name || (index === 0 ? 'Agregación analizada' : 'Grupo de referencia')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </LegendContainer>
        </ChartContainer>
    </Box>
    );
}

ChartRadar.propTypes = {
    title: PropTypes.string,
    series: PropTypes.array,
    categories: PropTypes.array,
};